<script lang="ts">
  import { iconAccessibility } from '../icons/index';
  import { t } from '../i18n/index';
  import { onMount } from 'svelte';

  let {
    isOpen,
    lang = 'en',
    onclick,
    el = $bindable<HTMLElement | null>(null),
  }: {
    isOpen: boolean;
    lang?: string;
    onclick: () => void;
    el?: HTMLElement | null;
  } = $props();

  let isDragging = $state(false);

  let activeCount = $derived(0); // badge removed, kept for future use

  onMount(() => {
    const btn = el;
    if (!btn) return;

    let dragStartX = 0;
    let dragStartY = 0;
    let startLeft = 0;
    let startTop = 0;
    let hasMoved = false;
    let pointerId: number | null = null;

    /** If the trigger was dragged (has inline left/top), re-clamp it to the
     *  current viewport. No-op when only CSS defaults are in effect — those
     *  already anchor to a viewport edge via bottom/right. */
    function clampToViewport(): void {
      if (!btn.style.left && !btn.style.top) return;
      const rect = btn.getBoundingClientRect();
      const w = rect.width || 56;
      const h = rect.height || 56;
      const maxLeft = Math.max(0, window.innerWidth - w);
      const maxTop = Math.max(0, window.innerHeight - h);
      const curLeft = parseFloat(btn.style.left) || rect.left;
      const curTop = parseFloat(btn.style.top) || rect.top;
      const nextLeft = Math.max(0, Math.min(maxLeft, curLeft));
      const nextTop = Math.max(0, Math.min(maxTop, curTop));
      if (nextLeft !== curLeft || nextTop !== curTop) {
        btn.style.left = nextLeft + 'px';
        btn.style.top = nextTop + 'px';
      }
    }

    function onDown(e: PointerEvent) {
      const rect = btn!.getBoundingClientRect();
      dragStartX = e.clientX;
      dragStartY = e.clientY;
      startLeft = rect.left;
      startTop = rect.top;
      hasMoved = false;
      pointerId = e.pointerId;
      btn!.setPointerCapture(e.pointerId);
    }

    function onMove(e: PointerEvent) {
      if (pointerId === null || e.pointerId !== pointerId) return;

      const dx = e.clientX - dragStartX;
      const dy = e.clientY - dragStartY;

      const dragThreshold = matchMedia('(pointer: coarse)').matches ? 15 : 5;
      if (!hasMoved && (Math.abs(dx) > dragThreshold || Math.abs(dy) > dragThreshold)) {
        hasMoved = true;
        isDragging = true;
        btn!.style.transition = 'none';
      }

      if (hasMoved) {
        const newLeft = Math.max(0, Math.min(window.innerWidth - 60, startLeft + dx));
        const newTop = Math.max(0, Math.min(window.innerHeight - 60, startTop + dy));
        btn!.style.left = newLeft + 'px';
        btn!.style.top = newTop + 'px';
        btn!.style.right = 'auto';
        btn!.style.bottom = 'auto';
      }
    }

    function onUp(e: PointerEvent) {
      if (pointerId === null || e.pointerId !== pointerId) return;
      btn!.releasePointerCapture(e.pointerId);
      btn!.style.transition = '';
      isDragging = false;
      pointerId = null;
      // Öffnen passiert NICHT hier, sondern im click-Handler unten.
      // Grund (Tap-Through-Bug auf Touch): öffnete das Panel schon beim
      // pointerup, feuerte der Browser den synthetischen Click DANACH auf das
      // Element, das dann unter dem Finger lag — die Feature-Karte im frisch
      // geöffneten Panel. Ein einziger Tap auf den Trigger aktivierte so
      // unbemerkt und dauerhaft „Links hervorheben". Wenn das Öffnen erst im
      // click-Handler passiert, konsumiert genau dieser Click das Öffnen:
      // beim Click-Dispatch ist der Trigger noch sichtbar, der Click trifft
      // ihn, das Panel öffnet, und es gibt keinen weiteren Click mehr.
    }

    function onClick() {
      // Nach einem Drag (hasMoved) ist der nachlaufende Click kein „Öffnen".
      // Tastatur (Enter/Space auf dem Button) feuert nur click, keine
      // Pointer-Events — hasMoved ist dann false und das Öffnen funktioniert.
      if (!hasMoved) onclick();
    }

    btn.addEventListener('pointerdown', onDown);
    btn.addEventListener('pointermove', onMove);
    btn.addEventListener('pointerup', onUp);
    btn.addEventListener('click', onClick);

    // Re-clamp inline drag position on window / visualViewport resize so
    // shrinking the window after a drag doesn't leave the trigger off-screen.
    window.addEventListener('resize', clampToViewport);
    window.visualViewport?.addEventListener('resize', clampToViewport);

    return () => {
      btn.removeEventListener('pointerdown', onDown);
      btn.removeEventListener('pointermove', onMove);
      btn.removeEventListener('pointerup', onUp);
      btn.removeEventListener('click', onClick);
      window.removeEventListener('resize', clampToViewport);
      window.visualViewport?.removeEventListener('resize', clampToViewport);
    };
  });
</script>

<button
  class="accessify-trigger"
  class:accessify-trigger--dragging={isDragging}
  class:accessify-trigger--hidden={isOpen}
  aria-expanded={isOpen}
  aria-label={isOpen ? t('widget.close', lang) : t('widget.open', lang)}
  bind:this={el}
>
  {@html iconAccessibility()}
</button>
